import { ErrorCode } from '@approvalmax/types';
import { constants } from '@approvalmax/utils';
import { useGetContactRole, UserRole } from 'shared/data/wallets/v1';
import { useGetCompanyMe } from 'shared/data/webApp/v2';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';

/**
 * Custom hook to get the current user's wallet role and related information for a specific company.
 *
 * This hook fetches the current user's information and their wallet role within the organization,
 * providing convenient flags to determine permissions and loading states.
 *
 * @param params - Configuration object for the hook
 * @param params.companyId - Optional company ID. If not provided, uses the company ID from the URL
 * @param params.queryOptions - Query options to pass to the useGetContactRole hook for customizing the API request
 *
 * @returns Object containing:
 * - `role`: The user's wallet role (UserRole.wallet_owner | UserRole.wallet_manager | UserRole.wallet_viewer | undefined)
 * - `isLoadingMe`: Boolean indicating if user data or contact role is still loading
 * - `isMeWalletsOwner`: Boolean flag indicating if the current user is a wallet owner
 */
export const useMeWalletsRole = ({
    companyId,
    queryOptions,
}: {
    companyId?: string;
    queryOptions?: Parameters<typeof useGetContactRole>[1];
} = {}) => {
    const { companyId: companyIdFromUrl } = useCompanyIdBasedOnUrl();
    const { data: me, isLoading: isLoadingMe } = useGetCompanyMe(
        { path: { companyId: companyId ?? companyIdFromUrl } },
        { staleTime: 5 * constants.time.minutesMs, cacheTime: 30 * constants.time.minutesMs }
    );
    const { data: contactRole, isLoading: isLoadingContactRole } = useGetContactRole(
        { path: { organizationId: companyId ?? companyIdFromUrl, contactId: me?.id } },
        queryOptions,
        { skipToastForErrorCodes: [ErrorCode.Forbidden, ErrorCode.OrganizationNotFound] }
    );

    return {
        role: contactRole?.role,
        isLoadingMe: isLoadingMe || isLoadingContactRole,
        isMeWalletsOwner: contactRole?.role === UserRole.wallet_owner,
    };
};
